'use client';
import { useState, useEffect } from 'react';

import Details from 'components/shared/BaseAccordion/Details';
import Divider from 'components/shared/Divider';
import Summary from 'components/shared/BaseAccordion/Summary';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { AccordionItemClassNames, AccordionItemProps } from 'libs/contentful/props';

import { getRichTextValue } from 'utils/formatters';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { cn, getSectionProps } from '@/utils/styles';
import RichTextHeadline from '../../RichTextHeadline/RichTextHeadline';

export enum StyleVariant {
  Default = 'Default',
  Secondary = 'Secondary',
}

const AccordionItem = ({
  sys,
  sectionName,
  accordionLabelRte,
  accordionCopy,
  isFirstElement,
  showSimpleListStyle,
  classNames,
  variant = StyleVariant.Default,
}: AccordionItemProps) => {
  const [open, setIsOpen] = useState(isFirstElement ?? false);

  const useSecondaryStyle = variant === StyleVariant.Secondary;

  const openIcon = useSecondaryStyle ? 'add_circle' : 'add';
  const closeIcon = useSecondaryStyle ? 'remove_circle' : 'remove';

  const labelText = accordionLabelRte ? String(getRichTextValue(accordionLabelRte)) : '';

  const internalClassNames: AccordionItemClassNames = {
    wrapper: cn({ 'pb-2 last-of-type:pb-0': useSecondaryStyle }, classNames?.wrapper),
    details: cn({ 'bg-bg-raised-secondary rounded-lg !m-0': useSecondaryStyle }, classNames?.details),
    summary: cn(
      'max-sm:text-body-saans text-heading-sm cursor-pointer',
      { 'm-0': useSecondaryStyle },
      classNames?.summary,
    ),
    headline: cn({ 'text-base': useSecondaryStyle }, classNames?.headline),
    copy: cn('font-serif text-body-serif pt-6', { 'text-base': useSecondaryStyle }, classNames?.copy),
  };

  const handleOnClick = (value: boolean) => {
    setIsOpen(value);
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && sectionName != null) {
      setIsOpen(isFirstElement || window.location.hash === `#${sectionName}`);

      window.addEventListener('hashchange', () => {
        if (window.location.hash === `#${sectionName}`) {
          setIsOpen(true);
        }
      });
    }
  }, [isFirstElement, sectionName]);

  const showDivider = !open || useSecondaryStyle;

  return (
    <div {...getSectionProps(undefined, undefined, sectionName, internalClassNames.wrapper)}>
      {showDivider && <Divider />}
      <Details className={internalClassNames.details} open={open}>
        <Summary
          className={internalClassNames?.summary}
          isActive={open}
          label={labelText}
          onClick={handleOnClick}
          openIcon={openIcon}
          closeIcon={closeIcon}
          {...liveUpdateAttrs(sys?.id, 'accordionLabelRte')}>
          <RichTextHeadline headline={accordionLabelRte} level="h3" className={internalClassNames.headline} />
        </Summary>
        <div className={internalClassNames.copy} {...liveUpdateAttrs(sys?.id, 'accordionCopy')}>
          <RichTextCopy copy={accordionCopy} showSimpleListStyle={showSimpleListStyle} />
        </div>
      </Details>
    </div>
  );
};

export default AccordionItem;
